/**
 * 시주(時柱) 계산.
 *
 * 각 지지는 2시간 단위에 대응한다:
 *   子(0): 23:00-00:59   丑(1): 01:00-02:59   寅(2): 03:00-04:59
 *   卯(3): 05:00-06:59   辰(4): 07:00-08:59   巳(5): 09:00-10:59
 *   午(6): 11:00-12:59   未(7): 13:00-14:59   申(8): 15:00-16:59
 *   酉(9): 17:00-18:59   戌(10): 19:00-20:59  亥(11): 21:00-22:59
 *
 * 야자시(23:00-23:59): 지지 = 子, 일주는 다음날로 전환.
 * 조자시(00:00-00:59): 지지 = 子, 일주는 당일 유지.
 *
 * 시간은 오자둔시법으로 산출한다:
 *   그룹 0 (甲己일) → 子시 시작 천간 甲(0)
 *   그룹 1 (乙庚일) → 子시 시작 천간 丙(2)
 *   그룹 2 (丙辛일) → 子시 시작 천간 戊(4)
 *   그룹 3 (丁壬일) → 子시 시작 천간 庚(6)
 *   그룹 4 (戊癸일) → 子시 시작 천간 壬(8)
 */

import type { Pillar } from '../types/pillars';
import type { HeavenlyStem } from '../types/stems';
import type { EarthlyBranch } from '../types/branches';
import { HOUR_STEM_START, HEAVENLY_STEMS, EARTHLY_BRANCHES } from '../core/constants';
import { makePillarFromStemBranch, stemIndex } from '../core/sexagenary';

/**
 * 시각(0-23)을 지지 인덱스(0-11)로 변환한다.
 *
 * @param hour - 시각 (0-23)
 * @returns 지지 인덱스 (0 = 子, 1 = 丑, ..., 11 = 亥)
 */
function hourToBranchIndex(hour: number): number {
  // 23:00-00:59 -> 子(0), 01:00-02:59 -> 丑(1), ..., 21:00-22:59 -> 亥(11)
  return Math.floor(((hour + 1) % 24) / 2);
}

/**
 * 시주를 계산한다.
 *
 * @param dayStem - 일주 천간 (야자시 보정 적용 후)
 * @param hour - 출생 시각 (0-23)
 * @returns 시주
 */
export function getHourPillar(dayStem: HeavenlyStem, hour: number): Pillar {
  const branchIdx = hourToBranchIndex(hour);
  const branch = EARTHLY_BRANCHES[branchIdx] as EarthlyBranch;

  // 오자둔시법: determine starting stem from day stem group
  const dayStemIdx = stemIndex(dayStem);
  const group = dayStemIdx % 5;
  const startStemIdx = HOUR_STEM_START[group];

  // Hour stem: start at 子 and offset by branch index
  const hourStemIdx = (startStemIdx + branchIdx) % 10;
  const stem = HEAVENLY_STEMS[hourStemIdx] as HeavenlyStem;

  return makePillarFromStemBranch(stem, branch);
}
